import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  PrimaryButton,
  DefaultButton,
  Card,
  makeStyles,
  Theme,
  useTheme,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Search, CheckCircle, Warning, AlertError } from '@bb-ui/icons/dist/small';
import { useSiteSearch, SearchType } from '../hooks/useSiteSearch';
import { SiteResponse } from '../hooks/useSiteFiltering';

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '700px',
    maxWidth: '900px',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  dialogPaperExpanded: {
    minWidth: '1000px',
    maxWidth: '1200px',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  dialogTitle: {
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  searchTypeField: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
    },
  },
  searchQueryField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
    },
  },
  searchButton: {
    minWidth: '120px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
  },
  resultsContainer: {
    minHeight: '300px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'center',
    minHeight: '300px',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    flexDirection: 'column',
    gap: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  resultCard: {
    padding: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
  },
  successCard: {
    border: `2px solid ${theme.palette.success.main}`,
    backgroundColor: `${theme.palette.success.light}10`,
  },
  warningCard: {
    border: `2px solid ${theme.palette.warning.main}`,
    backgroundColor: `${theme.palette.warning.light}10`,
  },
  errorCard: {
    border: `2px solid ${theme.palette.error.main}`,
    backgroundColor: `${theme.palette.error.light}10`,
  },
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  resultIcon: {
    fontSize: '24px',
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  siteCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  assignedLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  siteInfoHeader: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  siteInfoTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  siteDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: theme.spacing(1.5),
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.75),
  },
  detailLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
  },
  detailValue: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.3,
    wordBreak: 'break-all',
  },
  cohortInfo: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.b3,
    borderRadius: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  table: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  },
  cellText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '150px',
  },
  cohortChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  unassignedChip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  resultsCount: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginBottom: theme.spacing(2),
  },
  searchIcon: {
    fontSize: '1.2rem',
  },
}));

interface SiteSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const SiteSearchDialog: React.FC<SiteSearchDialogProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const {
    searchQuery,
    searchType,
    searchResults,
    searchError,
    hasSearched,
    isLoading,
    resultsCount,
    isSingleResult,
    isMultipleResults,
    setSearchQuery,
    setSearchType,
    performSearch,
    clearSearch,
    getCohortInfo,
  } = useSiteSearch();

  const [localQuery, setLocalQuery] = useState('');
  const [localType, setLocalType] = useState<SearchType>('captain_id');

  React.useEffect(() => {
    if (open) {
      setLocalQuery(searchQuery);
      setLocalType(searchType);
    } else {
      clearSearch();
      setLocalQuery('');
      setLocalType('captain_id');
    }
  }, [open, searchQuery, searchType, clearSearch]);

  const handleSearch = useCallback(() => {
    if (!localQuery.trim()) return;
    setSearchQuery(localQuery);
    setSearchType(localType);
    performSearch(localQuery, localType);
  }, [localQuery, localType, setSearchQuery, setSearchType, performSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
      e.preventDefault();
    }
  };

  const handleTypeChange = (newType: SearchType) => {
    setLocalType(newType);
    setLocalQuery('');
  };

  const getSearchPlaceholder = () => {
    switch (localType) {
      case 'captain_id':
        return t('cohorts.search.placeholders.captainId');
      case 'learn_url':
        return t('cohorts.search.placeholders.learnUrl');
      case 'client_name':
        return t('cohorts.search.placeholders.clientName');
      default:
        return t('cohorts.search.placeholders.default');
    }
  };

  const renderSiteCard = (site: SiteResponse) => {
    const cohortInfo = getCohortInfo(site);

    const cardClass =
      cohortInfo.status === 'assigned'
        ? `${classes.resultCard} ${classes.successCard}`
        : `${classes.resultCard} ${classes.warningCard}`;

    const icon =
      cohortInfo.status === 'assigned' ? (
        <CheckCircle className={`${classes.resultIcon} ${classes.successIcon}`} />
      ) : (
        <Warning className={`${classes.resultIcon} ${classes.warningIcon}`} />
      );

    return (
      <Card key={site.captain_id} className={cardClass} variant="outlined">
        <div className={classes.resultHeader}>
          {icon}
          {cohortInfo.status === 'assigned' && (
            <Typography className={classes.assignedLabel}>
              {t('cohorts.search.assignedToCohort')}
            </Typography>
          )}
          {cohortInfo.status === 'assigned' ? (
            <Chip
              label={cohortInfo.cohortName || cohortInfo.cohortId}
              className={classes.cohortChip}
            />
          ) : (
            <Chip
              label={t('cohorts.search.notAssignedToCohort')}
              className={classes.unassignedChip}
            />
          )}
        </div>

        <div className={classes.siteInfoHeader}>
          <Typography className={classes.siteInfoTitle}>
            {t('cohorts.search.siteInformation')}
          </Typography>
        </div>

        <div className={classes.siteDetails}>
          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.captainId')}</Typography>
            <Typography className={classes.detailValue}>{site.captain_id}</Typography>
          </div>

          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.learnUrl')}</Typography>
            <Typography className={classes.detailValue}>
              {site.learn_url || t('global.notApplicable')}
            </Typography>
          </div>

          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.learnId')}</Typography>
            <Typography className={classes.detailValue}>
              {site.learn_id || t('global.notApplicable')}
            </Typography>
          </div>

          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.clientName')}</Typography>
            <Typography className={classes.detailValue}>
              {site.client_name || t('global.notApplicable')}
            </Typography>
          </div>

          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.siteFleet')}</Typography>
            <Typography className={classes.detailValue}>
              {site.fleet_name || t('global.notApplicable')}
            </Typography>
          </div>

          <div className={classes.detailItem}>
            <Typography className={classes.detailLabel}>{t('cohorts.awsAccount')}</Typography>
            <Typography className={classes.detailValue}>
              {site.aws_account || t('global.notApplicable')}
            </Typography>
          </div>
        </div>

        {cohortInfo.status === 'assigned' && cohortInfo.downtimeDate && (
          <div className={classes.cohortInfo}>
            <div>
              <Typography variant="body1" color="textPrimary" style={{ fontWeight: 500 }}>
                {cohortInfo.displayText}
              </Typography>
              {cohortInfo.cohortName && cohortInfo.cohortId && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: '2px' }}>
                  ID: {cohortInfo.cohortId}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                {t('cohorts.downtimeDate')}:{' '}
                {new Date(cohortInfo.downtimeDate).toLocaleDateString()}
              </Typography>
            </div>
            <CheckCircle style={{ color: theme.palette.success.main, fontSize: '24px' }} />
          </div>
        )}
      </Card>
    );
  };

  const renderSitesTable = (sites: SiteResponse[]) => (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('cohorts.captainId')}</TableCell>
            <TableCell>{t('cohorts.learnUrl')}</TableCell>
            <TableCell>{t('cohorts.clientName')}</TableCell>
            <TableCell>{t('cohorts.siteFleet')}</TableCell>
            <TableCell>{t('cohorts.cohort')}</TableCell>
            <TableCell>{t('cohorts.downtimeDate')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sites.map((site) => {
            const cohortInfo = getCohortInfo(site);
            return (
              <TableRow key={site.captain_id}>
                <TableCell>
                  <Typography className={classes.cellText}>{site.captain_id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={classes.cellText}>{site.learn_url || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={classes.cellText}>{site.client_name || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography className={classes.cellText}>{site.fleet_name || '-'}</Typography>
                </TableCell>
                <TableCell>
                  {cohortInfo.status === 'assigned' ? (
                    <Chip
                      label={cohortInfo.cohortName || cohortInfo.cohortId}
                      size="small"
                      className={classes.cohortChip}
                    />
                  ) : (
                    <Chip
                      label={t('cohorts.search.unassigned')}
                      size="small"
                      className={classes.unassignedChip}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {cohortInfo.downtimeDate
                      ? new Date(cohortInfo.downtimeDate).toLocaleDateString()
                      : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress
            size={48}
            id="site-search-loading"
            label={t('cohorts.search.loading')}
          />
          <Typography variant="body2" color="textSecondary">
            {t('cohorts.search.searchWaitMessage')}
          </Typography>
        </div>
      );
    }

    if (searchError) {
      return (
        <Card className={`${classes.resultCard} ${classes.errorCard}`}>
          <div className={classes.resultHeader}>
            <AlertError className={`${classes.resultIcon} ${classes.errorIcon}`} />
            <Typography variant="h6">{t('cohorts.search.noResults')}</Typography>
          </div>
          <Typography variant="body1">{searchError}</Typography>
        </Card>
      );
    }

    if (!hasSearched) {
      return (
        <div className={classes.emptyContainer}>
          <Search style={{ fontSize: '48px', opacity: 0.5, marginBottom: '16px' }} />
          <Typography variant="body2">{t('cohorts.search.searchPlaceholder')}</Typography>
        </div>
      );
    }

    if (isSingleResult) {
      return renderSiteCard(searchResults[0]);
    }

    if (isMultipleResults) {
      return (
        <>
          <Typography variant="body2" className={classes.resultsCount} gutterBottom>
            {t('cohorts.search.resultsFound', { count: resultsCount })}
          </Typography>
          {renderSitesTable(searchResults)}
        </>
      );
    }

    return null;
  };

  const dialogPaperClass = isMultipleResults ? classes.dialogPaperExpanded : classes.dialogPaper;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: dialogPaperClass,
      }}
    >
      <DialogTitle onClose={onClose} className={classes.dialogTitle} closeLabel={t('global.close')}>
        <Search className={classes.searchIcon} />
        {t('cohorts.search.title')}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.searchContainer}>
          <TextField
            select
            label={t('cohorts.search.searchType')}
            value={localType}
            onChange={(e) => handleTypeChange(e.target.value as SearchType)}
            className={classes.searchTypeField}
            floatingLabel
            size="medium"
          >
            <MenuItem value="captain_id">{t('cohorts.search.types.captainId')}</MenuItem>
            <MenuItem value="learn_url">{t('cohorts.search.types.learnUrl')}</MenuItem>
            <MenuItem value="client_name">{t('cohorts.search.types.clientName')}</MenuItem>
          </TextField>

          <TextField
            label={t('cohorts.search.query')}
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={getSearchPlaceholder()}
            className={classes.searchQueryField}
            floatingLabel
            disabled={isLoading}
            size="medium"
          />

          <PrimaryButton
            onClick={handleSearch}
            disabled={!localQuery.trim() || isLoading}
            className={classes.searchButton}
          >
            {isLoading ? (
              t('cohorts.search.loading')
            ) : (
              <>
                <Search className={classes.searchIcon} />
                {t('cohorts.search.button')}
              </>
            )}
          </PrimaryButton>
        </div>

        <div className={classes.resultsContainer}>{renderResults()}</div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={clearSearch} disabled={isLoading || !hasSearched}>
          {t('cohorts.clearFilters')}
        </DefaultButton>
        <DefaultButton onClick={onClose} disabled={isLoading}>
          {t('global.close')}
        </DefaultButton>
      </DialogActions>
    </Dialog>
  );
};

export default SiteSearchDialog;
